import React, { useRef, useState } from "react";
import Dropdown from "../../../../UI/Dropdown/Dropdown";
import { SvgIcons } from "../../../SharedComponents/SvgIcons/SvgIcons";
import { HOURS, MINUTES, MERIDIEMS } from "./constants";
import styles from "./SchedulePicker.module.css";

const parseTime = (time) => {
  const match = time?.match?.(/^(\d{2}):(\d{2}) (AM|PM)$/);
  return match
    ? { hour: match[1], minute: match[2], meridiem: match[3] }
    : { hour: null, minute: null, meridiem: null };
};

/**
 * One class row's time control: the "hh:mm aa" button and its hh / mm / AM-PM
 * panel (minutes restricted to 00 and 30). Commits a "hh:mm AM" string.
 *
 * The panel is the shared portal Dropdown, so it escapes scrolling sections and
 * closes on an outside click or Escape.
 *
 * `clashes(time)` says whether a time runs into another class of the schedule;
 * such a time cannot be committed, so a repeat is refused as it is picked rather
 * than left for a blocker at the bottom of the page.
 */
const TimeSelect = ({ value, onChange, disabled, clashes }) => {
  const anchorRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState(() => parseTime(value));
  const isComplete = draft?.hour && draft?.minute && draft?.meridiem;
  const draftTime = isComplete ? `${draft?.hour}:${draft?.minute} ${draft?.meridiem}` : "";
  const isClash = !!draftTime && draftTime !== value && !!clashes?.(draftTime);

  const open = () => {
    setDraft(parseTime(value));
    setIsOpen(true);
  };

  const commit = () => {
    onChange?.(draftTime);
    setIsOpen(false);
  };

  const column = (options, key) => (
    <div className={styles.timeColumn}>
      {options?.map((option) => (
        <button
          key={option}
          type="button"
          className={draft?.[key] === option ? styles.timeOptionSelected : styles.timeOption}
          onClick={() => setDraft({ ...draft, [key]: option })}
        >
          {option}
        </button>
      ))}
    </div>
  );

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={styles.timeButton}
        disabled={disabled}
        aria-label="Class time"
        onClick={() => (isOpen ? setIsOpen(false) : open())}
      >
        <span className={value ? undefined : styles.placeholder}>{value ?? "hh:mm aa"}</span>
        <SvgIcons name="clock_icon" width="20" height="20" />
      </button>
      <Dropdown
        anchorRef={anchorRef}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        className={styles.timePopover}
        positionOptions={{ matchAnchorWidth: false }}
      >
        <div className={styles.timeColumns}>
          {column(HOURS, "hour")}
          {column(MINUTES, "minute")}
          {column(MERIDIEMS, "meridiem")}
        </div>
        {isClash && (
          <p className={styles.timeClash} role="alert">
            This time clashes with another class.
          </p>
        )}
        <div className={styles.timeActions}>
          <button type="button" className={styles.timeCancel} onClick={() => setIsOpen(false)}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.timeOk}
            disabled={!isComplete || isClash}
            onClick={commit}
          >
            OK
          </button>
        </div>
      </Dropdown>
    </>
  );
};

export default TimeSelect;
